import React, { forwardRef, useCallback, useRef, useState } from 'react';

import type { InputBaseProps } from '@mui/material';
import { IconButton, InputBase } from '@mui/material';
import clsx from 'clsx';
import { SquarePen } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import { AlineTooltip } from './AlineTooltip';
import { AutosizeInput } from './AutosizeInput';

const AutosizeInputAdapter = forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & { ownerState?: unknown }
>(function AutosizeInputAdapter({ className, ownerState: _ownerState, ...props }, ref) {
  return <AutosizeInput ref={ref} inputClassName={className} {...props} />;
});

/**
 * Auto-sizing inline input. Uses the local AutosizeInput component to handle
 * width measurement and passes through the full InputBaseProps API.
 */
export const InlineEditInput = forwardRef(function InlineEditInput(
  props: InputBaseProps,
  ref: React.Ref<HTMLInputElement>
) {
  return <InputBase ref={ref} inputComponent={AutosizeInputAdapter} {...props} />;
});

const useEditFieldStyles = makeStyles<void, 'iconButton'>()((theme, _params, classes) => ({
  clickable: {
    cursor: 'pointer',
  },
  /** Inline SVG sits on the baseline and adds descender space to the button. */
  iconButton: {
    '& svg': {
      display: 'block',
    },
    '&:focus-visible': {
      opacity: 1,
    },
    flexShrink: 0,
    opacity: 0,
    transition: 'opacity 120ms ease',
  },
  /** Holds the pencil's footprint while editing so the row keeps its width. */
  iconSpacer: {
    flexShrink: 0,
  },
  /**
   * Caller typography lives on the wrapping span (same class as display
   * mode). Beat MUI InputBase's own font-size / line-height / padding so the
   * edit box is the same height as the text it replaces.
   */
  input: {
    '& .MuiInputBase-input': {
      appearance: 'none',
      border: 0,
      boxSizing: 'border-box',
      font: 'inherit',
      height: 'auto',
      lineHeight: 'inherit',
      margin: 0,
      padding: 0,
    },
    '&.MuiInputBase-root': {
      color: 'inherit',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      lineHeight: 'inherit',
      padding: 0,
    },
    maxWidth: '100%',
    minWidth: 0,
  },
  row: {
    [`&:hover .${classes.iconButton}`]: {
      opacity: 1,
    },
    display: 'flex',
    flex: 1,
    gap: theme.spacing(0.5),
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  /**
   * Where the pencil sits against wrapped text, one class or the other so
   * no rule competes with a caller's. Two rules of equal specificity from
   * different sheets would be settled by insertion order, which is whichever
   * component happened to render first.
   */
  rowIconCenter: {
    alignItems: 'center',
  },
  rowIconStart: {
    alignItems: 'flex-start',
  },
  /**
   * The multiline edit box. It fills the row, so its text wraps at the same
   * width as the display text did and the row keeps its height; the
   * textarea itself has no padding, border, or resize handle, and lets the
   * wrapping span's typography through.
   */
  textarea: {
    '& .MuiInputBase-input': {
      overflowWrap: 'inherit',
      resize: 'none',
    },
    flexGrow: 1,
    width: '100%',
  },
  /**
   * Flex, not inline: an inline span's line-box strut is taller than the
   * input it wraps and would still shift the row when editing starts.
   */
  typographyWrap: {
    display: 'flex',
    maxWidth: '100%',
    minWidth: 0,
  },
  /** The wrap grows to the row while a multiline edit box is in it. */
  typographyWrapFill: {
    flexGrow: 1,
  },
}));

/** MUI's padding on a small IconButton, which sets the pencil's footprint. */
const ICON_BUTTON_PADDING_PX = 5;

interface InlineEditFieldProps {
  onSave: (value: string) => void;
  value: string;
  /**
   * `icon` reaches the pencil, and its spacer while editing: with `iconAlign`
   * "start", the caller offsets it to centre on its text's first line.
   */
  classes?: { display?: string; icon?: string; input?: string; placeholder?: string };
  disabled?: boolean;
  /**
   * When false, only the pencil starts editing so a parent row click still
   * fires. Default true: the display text is click-to-edit.
   */
  editOnDisplayClick?: boolean;
  editTooltip?: string;
  /**
   * "center" centres the pencil on the text block. "start" pins it to the
   * first line, for text that wraps on display.
   */
  iconAlign?: 'center' | 'start';
  iconSize?: number;
  initiallyEditing?: boolean;
  maxLength?: number;
  /**
   * Edit in a textarea that wraps at the display text's width, rather than a
   * single-line input that grows with its content. For text that wraps on
   * display, so starting an edit changes nothing about the layout. Enter
   * still commits; the value never holds a newline.
   */
  multiline?: boolean;
  /** Publish draft edits immediately; Escape restores the value from the start of editing. */
  onChange?: (value: string) => void;
  placeholder?: string;
  textPlaceholder?: string;
}

/**
 * Click-to-edit field with a SquarePen icon. The row fills its parent so
 * hovering anywhere on it reveals the pencil. Clicking the row (or the
 * pencil) switches to an auto-sized inline input. Saves on blur or Enter,
 * cancels on Escape.
 */
export function InlineEditField({
  classes: classesProp,
  disabled,
  editOnDisplayClick = true,
  editTooltip,
  iconAlign = 'center',
  iconSize = 14,
  initiallyEditing = false,
  maxLength,
  multiline = false,
  onChange,
  onSave,
  placeholder = 'Untitled',
  textPlaceholder = 'Untitled',
  value,
}: InlineEditFieldProps) {
  const draftRef = useRef(value);
  // Set once the edit ends, so the blur from unmounting the input does not save again.
  const closedRef = useRef(false);
  const { classes } = useEditFieldStyles();
  const originalValueRef = useRef(value);
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(initiallyEditing);

  const commit = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    setEditing(false);
    // A textarea accepts pasted line breaks; the value is one line.
    const trimmed = draftRef.current.replace(/\s*\n+\s*/g, ' ').trim();
    // Live edits already update value; still finalize them through onSave.
    if (onChange || trimmed !== value) onSave(trimmed);
  }, [onChange, onSave, value]);

  const cancel = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    const original = originalValueRef.current;
    draftRef.current = original;
    setDraft(original);
    onChange?.(original);
    setEditing(false);
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        // Commits rather than breaking the line in a textarea.
        e.preventDefault();
        commit();
      }
      if (e.key === 'Escape') cancel();
    },
    [cancel, commit]
  );

  const startEditing = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closedRef.current = false;
      originalValueRef.current = value;
      draftRef.current = value;
      setDraft(value);
      setEditing(true);
    },
    [value]
  );

  const changeDraft = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      draftRef.current = event.target.value;
      setDraft(event.target.value);
      onChange?.(event.target.value);
    },
    [onChange]
  );

  const iconFootprint = iconSize + ICON_BUTTON_PADDING_PX * 2;
  const rowClassName = clsx(
    classes.row,
    iconAlign === 'start' ? classes.rowIconStart : classes.rowIconCenter
  );

  if (editing) {
    const inputProps = {
      autoFocus: !disabled,
      className: clsx(classes.input, classesProp?.input),
      disabled,
      inputProps: { maxLength },
      onBlur: commit,
      onChange: changeDraft,
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
      onKeyDown: handleKeyDown,
      placeholder,
      value: draft,
    };
    return (
      <div className={rowClassName}>
        <span
          className={clsx(
            classes.typographyWrap,
            multiline && classes.typographyWrapFill,
            classesProp?.display
          )}
        >
          {multiline ? (
            <InputBase
              {...inputProps}
              multiline
              className={clsx(inputProps.className, classes.textarea)}
            />
          ) : (
            <InlineEditInput {...inputProps} sx={{ maxWidth: '100%', minWidth: 0 }} />
          )}
        </span>
        {!disabled && (
          <div
            className={clsx(classes.iconSpacer, classesProp?.icon)}
            style={{ height: iconFootprint, width: iconFootprint }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(rowClassName, !disabled && editOnDisplayClick && classes.clickable)}
      style={disabled ? { opacity: 0.5 } : undefined}
      onClick={disabled || !editOnDisplayClick ? undefined : startEditing}
    >
      <span
        className={clsx(
          classes.typographyWrap,
          classesProp?.display,
          !value && classesProp?.placeholder
        )}
      >
        {value || textPlaceholder}
      </span>
      {!disabled && (
        <InlineEditIconButton
          className={classesProp?.icon}
          editTooltip={editTooltip}
          iconSize={iconSize}
          onClick={startEditing}
        />
      )}
    </div>
  );
}

function InlineEditIconButton({
  className,
  editTooltip,
  iconSize,
  onClick,
}: {
  iconSize: number;
  onClick: (event: React.MouseEvent) => void;
  className?: string;
  editTooltip?: string;
}) {
  const { classes } = useEditFieldStyles();
  const button = (
    <IconButton className={clsx(classes.iconButton, className)} size="small" onClick={onClick}>
      <SquarePen size={iconSize} />
    </IconButton>
  );

  if (!editTooltip) return button;

  return (
    <AlineTooltip arrow placement="top" title={editTooltip}>
      {button}
    </AlineTooltip>
  );
}
